import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type {
  MyEventView,
  PageResponse,
  RespondRsvpRequest,
  RsvpResponse,
  SupportedLocale,
} from '@stm/contracts';
import { rsvpResponses } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { athleteDisplayName } from './athletes';
import {
  addCalendarDays,
  eventStatusKeys,
  eventTypeKeys,
  formatEventWhen,
  localDayOf,
  myEventsPath,
  rsvpKeys,
} from './scheduling';

export interface FamilyCalendarProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

const lookAheadDays = 60;

/** Published events of the person's own children (or own athlete record), with a yes/no/maybe per child. */
export function FamilyCalendar({
  locale,
  base,
  csrfToken,
}: FamilyCalendarProps): React.JSX.Element {
  const [events, setEvents] = useState<MyEventView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);

  const load = useCallback(async () => {
    const today = localDayOf(new Date());
    try {
      const page = await apiRequest<PageResponse<MyEventView>>(
        myEventsPath(base, today, addCalendarDays(today, lookAheadDays)),
      );
      setEvents(page.items);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function answer(
    event: MyEventView,
    athleteId: string,
    response: RsvpResponse,
  ): Promise<void> {
    setErrorKey(null);
    const body: RespondRsvpRequest = { athleteId, response };
    try {
      await apiRequest(`${base}/events/${event.id}/rsvp`, { method: 'PUT', body, csrfToken });
      setEvents(
        (current) =>
          current?.map((candidate) =>
            candidate.id === event.id
              ? {
                  ...candidate,
                  children: candidate.children.map((child) =>
                    child.athleteId === athleteId ? { ...child, rsvp: response } : child,
                  ),
                }
              : candidate,
          ) ?? null,
      );
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      // The deadline may have passed or the link changed; show the current state.
      await load();
    }
  }

  return (
    <Box component="section" aria-label={translate(locale, 'calendar.family.title')} sx={{ mt: 2 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {translate(locale, 'calendar.family.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {events === null && errorKey === null && (
        <Typography>{translate(locale, 'calendar.loading')}</Typography>
      )}
      {events?.length === 0 && (
        <Typography color="text.secondary">{translate(locale, 'calendar.family.empty')}</Typography>
      )}
      <Stack spacing={2}>
        {events?.map((event) => (
          <Paper key={event.id} variant="outlined" sx={{ padding: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h6" component="h3">
                {event.title}
              </Typography>
              <Chip size="small" label={translate(locale, eventTypeKeys[event.eventType])} />
              {event.status !== 'published' && (
                <Chip
                  size="small"
                  color={event.status === 'cancelled' ? 'error' : 'default'}
                  label={translate(locale, eventStatusKeys[event.status])}
                />
              )}
            </Box>
            <Typography>{formatEventWhen(event, locale)}</Typography>
            {(event.location !== null || event.resourceName !== null) && (
              <Typography color="text.secondary">
                {[event.resourceName, event.location].filter((part) => part !== null).join(' · ')}
              </Typography>
            )}
            {event.plan !== null && event.status === 'published' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" component="h4">
                  {translate(locale, 'calendar.family.plan')}: {event.plan.title}
                </Typography>
                <Stack component="ul" sx={{ margin: 0, paddingLeft: 3 }}>
                  {event.plan.blocks.map((block, position) => (
                    <Typography key={position} component="li" variant="body2">
                      {block.summary} ({block.durationMinutes}{' '}
                      {translate(locale, 'training.plan.minutes')})
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
            {event.status === 'cancelled' && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {translate(locale, 'calendar.family.cancelledNotice')}
                {event.cancelReason === null ? '' : ` ${event.cancelReason}`}
              </Alert>
            )}
            {event.status === 'published' && (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                {event.children.map((child) => (
                  <Box key={child.athleteId}>
                    <Typography sx={{ fontWeight: 500 }} id={`${event.id}-${child.athleteId}`}>
                      {athleteDisplayName(child, locale)} —{' '}
                      {translate(locale, 'calendar.family.attending')}
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      color="primary"
                      size="small"
                      disabled={!child.canRespond}
                      aria-labelledby={`${event.id}-${child.athleteId}`}
                      value={child.rsvp}
                      onChange={(_change, value: RsvpResponse | null) => {
                        if (value !== null) void answer(event, child.athleteId, value);
                      }}
                    >
                      {rsvpResponses.map((response) => (
                        <ToggleButton
                          key={response}
                          value={response}
                          sx={{ minHeight: 44, minWidth: 64 }}
                        >
                          {translate(locale, rsvpKeys[response])}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    {!child.canRespond && (
                      <Typography variant="body2" color="text.secondary">
                        {translate(locale, 'calendar.family.closed')}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
